import React from "react";
import styled from "styled-components";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import DropdownMenu from "../dropdown-menu/DropdownMenu";
import type { DropdownMenuItem } from "../dropdown-menu/DropdownMenu";
import type { CreditItem } from "../../app/services/credits";

// ===== styled =====

const Row = styled.div<{ $isDragging: boolean }>`
  width: 100%;
  display: flex;
  align-items: stretch;
  background: #fff;
  opacity: ${(props) => (props.$isDragging ? 0.5 : 1)};
  touch-action: none;
`;

const HandleBase = styled.div<{ $isDragging: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${(props) => (props.$isDragging ? "grabbing" : "grab")};
  user-select: none;
  text-align: center;
  border-radius: 4px;
  padding: 8px 12px;
  border-bottom: 1px solid var(--soremo-border);
  min-height: 40px;
`;

const SectionHandle = styled(HandleBase)`
  padding: 24px 12px 8px;
  border-bottom: 1px solid var(--soremo-border);
`;

const SubsectionHandle = styled(HandleBase)`
  padding: 16px 12px 6px;
  border-bottom: 1px dashed var(--soremo-border);
`;

const SpaceHandle = styled(HandleBase)`
  padding: 4px 12px;
  border-bottom: 1px dashed var(--soremo-border);
`;

const SpacePreview = styled.div<{ $lines: number }>`
  flex: 1;
  height: ${(props) => Math.max(1, props.$lines) * 16}px;
  border: 1px dashed var(--soremo-border);
  border-radius: 4px;
  background: repeating-linear-gradient(
    45deg,
    rgba(0, 0, 0, 0.02),
    rgba(0, 0, 0, 0.02) 4px,
    transparent 4px,
    transparent 8px
  );
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  padding: 0 8px;
  cursor: default;
  flex-shrink: 0;
`;

// 映画エンドロール風の 2 カラム配置（中央で左右に分かれる）
const EntryGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 16px 1fr;
  align-items: baseline;
`;

const EntryRole = styled.div`
  text-align: right;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--soremo-light-gray);
`;

const EntryDivider = styled.div`
  width: 1px;
  height: 12px;
  background: var(--soremo-border);
  justify-self: center;
`;

const EntryName = styled.div`
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CenteredStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 100%;
`;

// ===== component =====

export interface CreditItemRowProps {
  item: CreditItem;
  canEdit: boolean;
  language: "JP" | "EN";
  onAddSectionAbove: (item: CreditItem) => void;
  onAddSubsectionAbove: (item: CreditItem) => void;
  onEdit: (item: CreditItem) => void;
  onDelete: (item: CreditItem) => void;
}

const pickJpEn = (jp: string | undefined, en: string | undefined, lang: "JP" | "EN"): string => {
  if (lang === "JP") return jp || en || "";
  return en || jp || "";
};

const CreditItemRow: React.FC<CreditItemRowProps> = ({
  item,
  canEdit,
  language,
  onAddSectionAbove,
  onAddSubsectionAbove,
  onEdit,
  onDelete,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.item_id, disabled: !canEdit });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // section / subsection / entry のみ編集メニューを表示（space / track / image は対象外）
  const showEdit =
    item.item_type === "section" ||
    item.item_type === "subsection" ||
    item.item_type === "entry";

  const menuItems: DropdownMenuItem[] = canEdit
    ? [
        ...(showEdit
          ? [
              {
                label: "編集",
                onClick: () => onEdit(item),
                icon: "edit",
              },
            ]
          : []),
        {
          label: "上にセクションを追加",
          onClick: () => onAddSectionAbove(item),
          icon: "title",
        },
        {
          label: "上にサブセクションを追加",
          onClick: () => onAddSubsectionAbove(item),
          icon: "subtitles",
        },
        {
          label: "クレジットから削除",
          onClick: () => onDelete(item),
          icon: "delete",
        },
      ]
    : [];

  const dragProps = canEdit ? { ...attributes, ...listeners } : attributes;

  // ----- 描画分岐 -----

  const renderContent = () => {
    switch (item.item_type) {
      case "section": {
        const s = item.section;
        const title = pickJpEn(s?.title, s?.title_en, language);
        const note = pickJpEn(s?.note, s?.note_en, language);
        return (
          <SectionHandle $isDragging={isDragging} {...dragProps}>
            <CenteredStack>
              <div className="heading-spacing-18 u-line-height-150">
                {title || (language === "JP" ? "（無題セクション）" : "(Untitled section)")}
              </div>
              {note && (
                <div className="bodytext-11 u-text-light-gray u-line-height-150">
                  {note}
                </div>
              )}
            </CenteredStack>
          </SectionHandle>
        );
      }
      case "subsection": {
        const s = item.subsection;
        const title = pickJpEn(s?.title, s?.title_en, language);
        return (
          <SubsectionHandle $isDragging={isDragging} {...dragProps}>
            <CenteredStack>
              <div className="heading-spacing u-line-height-100">
                {title || (language === "JP" ? "（無題サブセクション）" : "(Untitled subsection)")}
              </div>
            </CenteredStack>
          </SubsectionHandle>
        );
      }
      case "entry": {
        const e = item.entry;
        const role = pickJpEn(e?.role, e?.role_en, language);
        const name = pickJpEn(e?.display_name, e?.display_name_en, language);
        return (
          <HandleBase $isDragging={isDragging} {...dragProps}>
            <EntryGrid>
              <EntryRole className="bodytext-13 u-line-height-150">
                {role}
              </EntryRole>
              <EntryDivider />
              <EntryName className="heading-13 u-line-height-150">
                {name || (language === "JP" ? "（表示名未設定）" : "(no name)")}
              </EntryName>
            </EntryGrid>
          </HandleBase>
        );
      }
      case "space": {
        const lines = item.space?.lines ?? 1;
        return (
          <SpaceHandle $isDragging={isDragging} {...dragProps}>
            <SpacePreview $lines={lines}>
              <span className="bodytext-11 u-text-light-gray">
                {lines}行のスペース
              </span>
            </SpacePreview>
          </SpaceHandle>
        );
      }
      case "track":
      case "image":
      default: {
        return (
          <HandleBase $isDragging={isDragging} {...dragProps}>
            <CenteredStack>
              <div className="bodytext-11 u-text-light-gray u-line-height-100">
                {item.item_type}（このタイプはまだ編集に対応していません）
              </div>
            </CenteredStack>
          </HandleBase>
        );
      }
    }
  };

  return (
    <Row ref={setNodeRef} style={style} $isDragging={isDragging}>
      {renderContent()}
      {canEdit && menuItems.length > 0 && (
        <Actions>
          <DropdownMenu items={menuItems} />
        </Actions>
      )}
    </Row>
  );
};

export default CreditItemRow;
